import { Box, Stack, Switch, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import TestingToolLayout from "~/features/testingTools/TestingToolLayout";
import {
  addEvent,
  resetSingleStepSession,
  selectBinToPortTestSession,
  selectIsBinToPortLoopingEnabled,
  setSingleStepTestStatus
} from "~/features/testingTools/testingTools.slice";
import { TestingTool } from "~/features/testingTools/testingTools.types";
import { useGridV2Subscription } from "~/lib/signalr";
import {
  selectThisWorkstation,
  selectWorkstationAvailablePorts
} from "~/redux/selectors/workstationsSelectors";

import WorkstationPort from "./WorkstationPort";

type BinToPortTestProps = {
  onTestingSessionStart: (testType: TestingTool) => Promise<void>;
};

const BinToPortTest = ({ onTestingSessionStart }: BinToPortTestProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [binToPortTestLoopingEnabled, enableBinToPortTestLooping] =
    useState<boolean>(false);
  const workstation = useAppSelector(selectThisWorkstation);
  const workstationAvailablePorts = useAppSelector(
    selectWorkstationAvailablePorts
  );
  const columns = workstation?.multiPortEnabled
    ? workstationAvailablePorts.length + 1
    : workstationAvailablePorts.length;

  const ports = useAppSelector(selectWorkstationAvailablePorts);
  const binToPortTestSession = useAppSelector(selectBinToPortTestSession);
  const isBinToPortLoopReady = useAppSelector(selectIsBinToPortLoopingEnabled);
  const parentPortId = workstation?.ports?.[0]?.parentPortId || null;

  useEffect(() => {
    //On mount update the bin to port test status
    //and set port readiness map
    if (!binToPortTestSession?.portReadinessMap) {
      dispatch(
        setSingleStepTestStatus({
          testingToolName: "Bin to Port",
          eventStatus: "Info",
          skipLogging: true,
          additionalProps: {
            portIdList: ports
          }
        })
      );
    }
    return () => {
      dispatch(resetSingleStepSession("Bin to Port"));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useGridV2Subscription((event) => {
    if (
      event.case === "PortError" &&
      event.event.hasError &&
      event.event.portId &&
      ports.includes(event.event.portId)
    ) {
      dispatch(
        addEvent({
          text: `Log Publisher: Port error for port ${event.event.portId}`,
          state: "Failed",
          testOrigin: "Bin to Port"
        })
      );
      //make sure to update bin to port test status
      //skip logging since we inidividually log the error
      dispatch(
        setSingleStepTestStatus({
          testingToolName: "Bin to Port",
          eventStatus: "Failed",
          skipLogging: true
        })
      );
    }
    if (
      event.case === "BinModeChange" &&
      event.event.binMode === "O" &&
      event.event.portId &&
      ports.includes(event.event.portId)
    ) {
      dispatch(
        addEvent({
          text: `Log Publisher: Bin ${event.event.binId} arrived at port ${event.event.portId}`,
          state: "Info",
          testOrigin: "Bin to Port"
        })
      );
    }
  });

  return (
    <TestingToolLayout>
      <Stack
        sx={{
          gap: 4,
          alignItems: "center",
          justifyContent: "center",
          height: "100%"
        }}
        aria-label="Bin to port test content"
        role="region"
      >
        <Box
          sx={{
            display: "grid",
            placeContent: "center",
            gridAutoFlow: "column",
            gridTemplateColumns: `repeat(${columns}, minmax(250px, 350px))`,
            gridTemplateRows: parentPortId ? "auto 1fr" : "auto",
            width: "100%",
            gap: 4,
            "@media (max-width: 900px)": {
              gridTemplateColumns: "1fr",
              gap: 2,
              gridAutoFlow: "row"
            }
          }}
        >
          {parentPortId && (
            <Typography
              variant="h4"
              fontWeight={500}
              textAlign="center"
              gridColumn="span 2"
            >
              {`${t("Parent Port")} ${parentPortId}`}
            </Typography>
          )}
          {ports.map((portId) => (
            <WorkstationPort
              key={portId}
              portId={portId}
              binToPortTestLoopingEnabled={binToPortTestLoopingEnabled}
              //this method is used to disabled bin to port looping
              //when port is not ready or has errors
              updateBinToPortLoopBtnStatus={(statusValue: boolean) => {
                if (statusValue !== binToPortTestLoopingEnabled)
                  enableBinToPortTestLooping(statusValue);
              }}
            />
          ))}
        </Box>
        <Stack
          sx={{
            gap: 1,
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap"
          }}
        >
          <label
            style={{
              fontSize: "1.2rem"
            }}
            htmlFor="enable port looping button"
          >
            {t("enable bin to port looping")}
          </label>
          <Switch
            inputProps={{
              id: "enable port looping button"
            }}
            sx={{
              cursor: isBinToPortLoopReady ? "default" : "not-allowed"
            }}
            checked={binToPortTestLoopingEnabled}
            // Use MUI checked arg (avoids React event pooling)
            onChange={async (_e, checked) => {
              await onTestingSessionStart("Bin to Port");
              // just update bin to port test status to running
              // skip event logging since we log the event in the TestingtoolsView component
              dispatch(
                setSingleStepTestStatus({
                  testingToolName: "Bin to Port",
                  eventStatus: "Info",
                  skipLogging: true
                })
              );
              enableBinToPortTestLooping(checked);
            }}
            disableRipple
            disabled={!isBinToPortLoopReady}
          />
        </Stack>
      </Stack>
    </TestingToolLayout>
  );
};

export default BinToPortTest;
